import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { CompaniesService } from '../../companies/data-access/companies.service';
import { CompanyItem } from '../../companies/data-access/companies.models';
import {
  CompanyRetentionPolicyDto,
  DataRetentionPoliciesDto,
  DataRetentionRunSummaryDto,
  GlobalRetentionPolicyDto,
} from '../data-access/data-retention.models';
import { DataRetentionService } from '../data-access/data-retention.service';
import {
  DataViewShellComponent,
  ErrorBlockComponent,
  PageContextGuardComponent,
  PageShellComponent,
  TabStripComponent,
  TabStripItem,
} from '@clinic-platform/ui-components';

@Component({
  selector: 'app-data-retention-page',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    PageHeaderComponent,
    CompanyContextRequiredComponent,
    DataViewShellComponent,
    ErrorBlockComponent,
    PageContextGuardComponent,
    PageShellComponent,
    TabStripComponent,
  ],
  template: `
    <lib-page-shell>
      <app-page-header
        title="Data Retention"
        subtitle="Control appointment and slot retention (system admin only)"
      />

      <lib-tab-strip
        [items]="retentionTabs"
        [activeId]="activeRetentionTab"
        (activeIdChange)="selectRetentionTab($event)"
        ariaLabel="Data retention sections"
      />

      @if (activeRetentionTab === 'global') {
      <section class="retention__card retention__card--tabbed">
        <h3 class="retention__title">Global retention bounds</h3>
        <lib-data-view-shell [isLoading]="isPoliciesLoading" [hasError]="false" [isEmpty]="false">
          <div dataViewLoading class="retention__meta">Loading policies...</div>

          <form [formGroup]="globalForm" (ngSubmit)="saveGlobal()" class="retention__grid">
            <label>
              Min archive after (days)
              <input type="number" min="1" max="36500" formControlName="minArchiveAfterDays" />
            </label>
            <label>
              Max archive after (days)
              <input type="number" min="1" max="36500" formControlName="maxArchiveAfterDays" />
            </label>
            <label>
              Min purge after (days)
              <input type="number" min="1" max="36500" formControlName="minPurgeAfterDays" />
            </label>
            <label>
              Max purge after (days)
              <input type="number" min="1" max="36500" formControlName="maxPurgeAfterDays" />
            </label>
            <button type="submit" [disabled]="globalForm.invalid || isSavingGlobal">
              {{ isSavingGlobal ? 'Saving...' : 'Save global bounds' }}
            </button>
          </form>
        </lib-data-view-shell>

        @if (globalSource) {
          <div class="retention__meta">Source: {{ globalSource }}</div>
        }

        @if (globalError) {
          <lib-error-block variant="error" [message]="(globalError | t) ?? ''" />
        }

        @if (globalSuccess) {
          <lib-error-block variant="success" [message]="globalSuccess" />
        }
      </section>
      }

      @if (activeRetentionTab === 'company') {
      <section class="retention__card retention__card--tabbed">
        <h3 class="retention__title">Company retention policy</h3>
        <div class="retention__meta">Company: {{ selectedCompanyLabel }}</div>

        <lib-page-context-guard [blocked]="!selectedCompanyId">
          <app-company-context-required />
        </lib-page-context-guard>

        @if (selectedCompanyId) {
          <lib-data-view-shell [isLoading]="isPoliciesLoading" [hasError]="false" [isEmpty]="false">
            <div dataViewLoading class="retention__meta">Loading policies...</div>

            <form [formGroup]="companyForm" (ngSubmit)="saveCompany()" class="retention__grid">
              <label>
                Archive after (days)
                <input type="number" min="1" max="36500" formControlName="archiveAfterDays" />
              </label>
              <label>
                Purge after (days)
                <input type="number" min="1" max="36500" formControlName="purgeAfterDays" />
              </label>
              <label class="retention__checkbox">
                <input type="checkbox" formControlName="isEnabled" />
                Retention enabled
              </label>
              <button type="submit" [disabled]="companyForm.invalid || isSavingCompany || !selectedCompanyId">
                {{ isSavingCompany ? 'Saving...' : 'Save company policy' }}
              </button>
              <button type="button" class="retention__secondary" [disabled]="isRunning || !selectedCompanyId"
                (click)="runCompany()">
                {{ isRunning ? 'Running...' : 'Run retention now' }}
              </button>
            </form>
          </lib-data-view-shell>
        }

        @if (companySource) {
          <div class="retention__meta">Source: {{ companySource }}</div>
        }

        @if (companyError) {
          <lib-error-block variant="error" [message]="(companyError | t) ?? ''" />
        }

        @if (companySuccess) {
          <lib-error-block variant="success" [message]="companySuccess" />
        }
      </section>
      }
    </lib-page-shell>
  `,
  styles: [`
    :host {
      display: block;
    }

    .retention__card {
      margin-bottom: 16px;
      padding: 16px;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      background: #ffffff;
    }

    .retention__card--tabbed {
      border-top: none;
      border-top-left-radius: 0;
      margin-top: 0;
    }

    .retention__title {
      margin: 0 0 12px;
      font-size: 15px;
      font-weight: 700;
      color: #111827;
    }

    .retention__grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
      align-items: end;
    }

    .retention__grid label {
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 13px;
      color: #374151;
      font-weight: 600;
    }

    .retention__grid input[type='number'],
    .retention__grid select {
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 10px;
      box-sizing: border-box;
      font-size: 14px;
      color: #111827;
      background: #ffffff;
    }

    .retention__checkbox {
      flex-direction: row !important;
      align-items: center;
      gap: 8px !important;
      min-height: 40px;
      font-weight: 500 !important;
    }

    .retention__grid button {
      height: 40px;
      border: none;
      border-radius: 8px;
      background: #2563eb;
      color: #ffffff;
      padding: 0 14px;
      cursor: pointer;
    }

    .retention__grid button:disabled {
      opacity: 0.6;
      cursor: default;
    }

    .retention__secondary {
      background: #111827 !important;
    }

    .retention__meta {
      margin-top: 12px;
      color: #6b7280;
      font-size: 13px;
    }

    @media (max-width: 900px) {
      .retention__grid {
        grid-template-columns: 1fr;
      }

      .retention__grid button {
        width: 100%;
      }
    }

    @media (max-width: 640px) {
      .retention__card {
        padding: 12px;
      }
    }
  `],
})
export class DataRetentionPageComponent implements OnInit, OnDestroy {
  private static readonly DEFAULT_COMPANY_ARCHIVE_DAYS = 180;
  private static readonly DEFAULT_COMPANY_PURGE_DAYS = 365;

  private readonly dataRetentionService = inject(DataRetentionService);
  private readonly companiesService = inject(CompaniesService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly subscriptions = new Subscription();

  protected companies: CompanyItem[] = [];
  protected policies: DataRetentionPoliciesDto | null = null;
  protected activeRetentionTab: 'global' | 'company' = 'global';
  protected retentionTabs: TabStripItem[] = [];

  protected readonly globalForm = this.formBuilder.group({
    minArchiveAfterDays: this.formBuilder.nonNullable.control(30, [
      Validators.required,
      Validators.min(1),
      Validators.max(36500),
    ]),
    maxArchiveAfterDays: this.formBuilder.nonNullable.control(3650, [
      Validators.required,
      Validators.min(1),
      Validators.max(36500),
    ]),
    minPurgeAfterDays: this.formBuilder.nonNullable.control(90, [
      Validators.required,
      Validators.min(1),
      Validators.max(36500),
    ]),
    maxPurgeAfterDays: this.formBuilder.nonNullable.control(3650, [
      Validators.required,
      Validators.min(1),
      Validators.max(36500),
    ]),
  });

  protected readonly companyForm = this.formBuilder.group({
    archiveAfterDays: this.formBuilder.nonNullable.control(180, [
      Validators.required,
      Validators.min(1),
      Validators.max(36500),
    ]),
    purgeAfterDays: this.formBuilder.nonNullable.control(365, [
      Validators.required,
      Validators.min(1),
      Validators.max(36500),
    ]),
    isEnabled: this.formBuilder.nonNullable.control(false),
  });

  protected selectedCompanyId: string | null = null;
  protected globalSource: GlobalRetentionPolicyDto['source'] | null = null;
  protected companySource: CompanyRetentionPolicyDto['source'] | null = null;

  protected isSavingGlobal = false;
  protected isSavingCompany = false;
  protected isRunning = false;
  protected isPoliciesLoading = false;

  protected globalError: string | null = null;
  protected companyError: string | null = null;
  protected globalSuccess: string | null = null;
  protected companySuccess: string | null = null;

  protected get selectedCompanyLabel(): string {
    const selectedCompanyId = this.selectedCompanyId?.trim() ?? '';
    if (!selectedCompanyId) {
      return 'Not selected';
    }

    const company = this.companies.find((item) => item.companyId === selectedCompanyId) ?? null;
    if (company) {
      const displayName = company.brandName || company.legalName;
      return `${displayName} (${company.slug})`;
    }

    return selectedCompanyId;
  }

  public ngOnInit(): void {
    this.selectedCompanyId = this.systemAdminContext.getCompanyId();
    this.rebuildRetentionTabs();

    this.subscriptions.add(this.systemAdminContext.companyId$.subscribe((companyId) => {
      this.selectedCompanyId = companyId?.trim() || null;
      this.rebuildRetentionTabs();
      this.syncCompanyForm();
    }));

    this.load();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected selectRetentionTab(tabId: string): void {
    if (tabId === 'global' || tabId === 'company') {
      this.activeRetentionTab = tabId;
    }
  }

  protected saveGlobal(): void {
    if (this.globalForm.invalid || this.isSavingGlobal) {
      return;
    }

    this.isSavingGlobal = true;
    this.globalError = null;
    this.globalSuccess = null;

    this.dataRetentionService.updateGlobalPolicy(this.globalForm.getRawValue()).subscribe({
      next: (policy) => {
        this.globalSource = policy.source;
        this.globalSuccess = 'Global retention bounds saved';
        this.isSavingGlobal = false;
        this.refreshPolicies();
      },
      error: (error: unknown) => {
        this.globalError = resolveHttpErrorMessageKey(error, { fallback: 'error.generic' });
        this.isSavingGlobal = false;
      }
    });
  }

  protected saveCompany(): void {
    if (!this.selectedCompanyId || this.companyForm.invalid || this.isSavingCompany) {
      return;
    }

    this.isSavingCompany = true;
    this.companyError = null;
    this.companySuccess = null;

    this.dataRetentionService
      .updateCompanyPolicy(this.selectedCompanyId, this.companyForm.getRawValue())
      .subscribe({
        next: (policy) => {
          this.companySource = policy.source;
          this.companySuccess = 'Company retention policy saved';
          this.isSavingCompany = false;
          this.refreshPolicies();
        },
        error: (error: unknown) => {
          this.companyError = resolveHttpErrorMessageKey(error, { fallback: 'error.generic' });
          this.isSavingCompany = false;
        }
      });
  }

  protected runCompany(): void {
    if (!this.selectedCompanyId || this.isRunning) {
      return;
    }

    this.isRunning = true;
    this.companyError = null;
    this.companySuccess = null;

    this.dataRetentionService.runCompanyRetention(this.selectedCompanyId).subscribe({
      next: (summary: DataRetentionRunSummaryDto) => {
        this.companySuccess =
          `Run finished (${summary.status}). Archived ${summary.archivedCount}, purged ${summary.purgedCount}.`;
        this.isRunning = false;
      },
      error: (error: unknown) => {
        this.companyError = resolveHttpErrorMessageKey(error, { fallback: 'error.generic' });
        this.isRunning = false;
      }
    });
  }

  private load(): void {
    this.globalError = null;
    this.companyError = null;

    this.companiesService.getList().subscribe({
      next: (companies) => {
        this.companies = companies;
        this.rebuildRetentionTabs();
      },
      error: () => {
        this.companies = [];
        this.rebuildRetentionTabs();
      }
    });

    this.refreshPolicies();
  }

  private refreshPolicies(): void {
    this.isPoliciesLoading = true;
    this.dataRetentionService.getPolicies().subscribe({
      next: (policies) => {
        this.policies = policies;
        this.patchGlobalForm(policies.global);
        this.syncCompanyForm();
        this.isPoliciesLoading = false;
      },
      error: (error: unknown) => {
        this.globalError = resolveHttpErrorMessageKey(error, { fallback: 'error.generic' });
        this.isPoliciesLoading = false;
      }
    });
  }

  private patchGlobalForm(policy: GlobalRetentionPolicyDto): void {
    this.globalForm.patchValue({
      minArchiveAfterDays: policy.minArchiveAfterDays,
      maxArchiveAfterDays: policy.maxArchiveAfterDays,
      minPurgeAfterDays: policy.minPurgeAfterDays,
      maxPurgeAfterDays: policy.maxPurgeAfterDays,
    });
    this.globalSource = policy.source;
  }

  private syncCompanyForm(): void {
    if (!this.policies) {
      this.companySource = null;
      return;
    }

    if (!this.selectedCompanyId) {
      this.patchDefaultCompanyPolicy(this.policies.global);
      this.companySource = null;
      return;
    }

    const policy = this.policies.companies.find((item) => item.companyId === this.selectedCompanyId) ?? null;
    const global = this.policies.global;

    if (policy) {
      this.companyForm.patchValue({
        archiveAfterDays: policy.archiveAfterDays,
        purgeAfterDays: policy.purgeAfterDays,
        isEnabled: policy.isEnabled,
      });
      this.companySource = policy.source;
      return;
    }

    this.patchDefaultCompanyPolicy(global);
    this.companySource = 'default';
  }

  private patchDefaultCompanyPolicy(global: GlobalRetentionPolicyDto): void {
    const defaultArchive = Math.max(
      global.minArchiveAfterDays,
      DataRetentionPageComponent.DEFAULT_COMPANY_ARCHIVE_DAYS,
    );
    const defaultPurge = Math.max(
      global.minPurgeAfterDays,
      DataRetentionPageComponent.DEFAULT_COMPANY_PURGE_DAYS,
      defaultArchive,
    );

    this.companyForm.patchValue({
      archiveAfterDays: defaultArchive,
      purgeAfterDays: defaultPurge,
      isEnabled: false,
    });
  }

  private rebuildRetentionTabs(): void {
    const hasCompany = !!this.selectedCompanyId?.trim();
    const companyLabel = hasCompany ? this.selectedCompanyLabel : 'Company';

    this.retentionTabs = [
      { id: 'global', label: 'Global' },
      { id: 'company', label: companyLabel, disabled: !hasCompany },
    ];

    if (!hasCompany && this.activeRetentionTab === 'company') {
      this.activeRetentionTab = 'global';
    }
  }
}
